import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Globe, Leaf, BookOpen, HandHeart } from 'lucide-react';

const initiatives = [
  {
    icon: Heart,
    title: 'Community Weddings',
    description: 'We sponsor and organize weddings for underprivileged couples, ensuring every love story gets its perfect celebration.',
    image: '/pin2.jpg',
    impact: '50+ couples supported annually'
  },
  {
    icon: BookOpen,
    title: 'Education Support',
    description: 'Providing scholarships and educational resources to children from economically disadvantaged backgrounds.',
    image: '/pin3.jpg',
    impact: '200+ students supported'
  },
  {
    icon: Leaf,
    title: 'Sustainable Practices',
    description: 'Implementing eco-friendly wedding solutions, reducing waste, and promoting sustainable celebration practices.',
    image: '/pin4.jpg',
    impact: '60% waste reduction achieved'
  },
  {
    icon: HandHeart,
    title: 'Local Artisan Support',
    description: 'Partnering with local craftspeople and artisans to preserve traditional arts while providing sustainable livelihoods.',
    image: '/pin8.jpg',
    impact: '100+ artisans empowered'
  },
  {
    icon: Users,
    title: 'Women Empowerment',
    description: 'Training and employing women from rural areas in event management and hospitality services.',
    image: '/pin9.jpg',
    impact: '150+ women trained'
  },
  {
    icon: Globe,
    title: 'Cultural Preservation',
    description: 'Documenting and promoting traditional wedding customs and cultural practices across different regions.',
    image: '/pin10.jpg',
    impact: '25+ traditions documented'
  }
];

const stats = [
  { value: '₹2.5 Cr+', label: 'Invested in CSR Activities', desc: 'Annual commitment to social causes' },
  { value: '10,000+', label: 'Lives Impacted', desc: 'Direct beneficiaries of our programs' },
  { value: '50+', label: 'Partner NGOs', desc: 'Collaborative partnerships for greater impact' },
  { value: '15+', label: 'States Covered', desc: 'Pan-India presence in CSR activities' }
];

export default function CSR() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play().catch((err) => console.log('Video play failed', err));
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] max-h-[900px] flex items-center justify-center overflow-hidden pt-20 sm:pt-0">
        <div className="absolute inset-0">
          <img
            src="/pin7.jpg"
            alt="CSR Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
        </div>

        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-[10px] sm:text-xs uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[#F1D291] mb-6 sm:mb-8"
          >
            Corporate Social Responsibility
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif mb-6 sm:mb-8 leading-tight"
          >
            Celebrating Love, Spreading Joy
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed"
          >
            At YPD Weddings, we believe that every celebration should contribute to a better world.
            Our commitment extends beyond creating beautiful weddings to building stronger communities
            and preserving cultural heritage.
          </motion.p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-16"
          >
            <div className="text-xs uppercase tracking-[0.4em] text-[#395958] mb-6">Our Mission</div>
            <h2 className="text-3xl md:text-5xl font-serif text-gray-900 mb-8 leading-tight">
              Beyond Beautiful Weddings
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              We are committed to using our expertise, resources, and network to create positive social impact.
              Through our CSR initiatives, we aim to make weddings not just memorable for couples, but meaningful
              for communities, sustainable for the environment, and supportive of cultural preservation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CSR Statistics */}
      <section className="py-20 bg-gradient-to-r from-[#395958] to-[#2a4342]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-16"
          >
            <div className="text-xs uppercase tracking-[0.4em] text-[#F1D291] mb-6">Impact Metrics</div>
            <h2 className="text-3xl md:text-5xl font-serif text-white mb-8 leading-tight">
              Measuring Our Impact
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: index * 0.1 }}
                viewport={{ once: true, margin: '-100px' }}
                className="text-center text-white"
              >
                <div className="text-4xl md:text-5xl font-serif text-[#F1D291] mb-4">
                  {stat.value}
                </div>
                <div className="text-lg font-medium mb-2">{stat.label}</div>
                <div className="text-sm text-white/70">{stat.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CSR Initiatives */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-20"
          >
            <div className="text-xs uppercase tracking-[0.4em] text-[#395958] mb-6">Our Initiatives</div>
            <h2 className="text-3xl md:text-5xl font-serif text-gray-900 mb-8 leading-tight">
              Creating Positive Change
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our comprehensive CSR programs focus on community development, environmental sustainability,
              and cultural preservation while leveraging our expertise in event management.
            </p>
          </motion.div>

          {/* Featured Initiative Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: '-100px' }}
            className="bg-white rounded-lg shadow-2xl overflow-hidden"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Image Section */}
              <div className="relative h-64 lg:h-auto min-h-[400px]">
                <img
                  src="/navya.avif"
                  alt="Navya Naveli Nanda Entreprenaari Mela"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#F1D291] rounded-full flex items-center justify-center">
                      <HandHeart size={24} className="text-[#395958]" />
                    </div>
                    <span className="text-xs uppercase tracking-[0.3em] text-white/90 font-medium">
                      Featured Initiative
                    </span>
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-6 leading-tight">
                  Empowering Women, One Platform at a Time
                </h3>

                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    At YPD Weddings, we believe that meaningful impact begins with meaningful partnerships.
                    Which is why we were honoured to play a part in <strong className="text-gray-900">Navya Naveli Nanda's Entreprenaari Mela</strong>,
                    an inspiring initiative by Aspire for Her and Project Naveli dedicated to advancing women's
                    economic and social equality.
                  </p>

                  <p>
                    Across two vibrant days, <strong className="text-gray-900">120 women entrepreneurs</strong> came together to showcase their
                    talent, passion, and entrepreneurial spirit. From handcrafted fashion to curated décor and
                    artisanal foods, the Mela stood as a testament to what women can build when given the space,
                    opportunity, and community to thrive.
                  </p>

                  <p>
                    The event's most memorable moment—the <strong className="text-gray-900">"Gulabo" fashion showcase by Abu Jani Sandeep Khosla</strong>—featured
                    entrepreneurs walking the ramp alongside admired public figures. It wasn't just a runway;
                    it was a celebration of ambition, resilience, and the collective progress of women across India.
                  </p>

                  <p className="text-[#395958] font-medium">
                    Our involvement in this initiative reflects our broader CSR vision: to support platforms
                    that uplift women, nurture creativity, and contribute to a more equitable future.
                  </p>
                </div>

                <blockquote className="my-8 pl-6 border-l-4 border-[#F1D291] italic text-gray-700 text-lg">
                  "It was amazing, everything he touches turns to gold, and it looks incredible, as you can see, it's looking like Diwali and the festive season. Thank you so much for making it look so beautiful."
                  <footer className="mt-2 text-sm font-medium text-[#395958] not-italic">— Navya Naveli Nanda</footer>
                </blockquote>

                {/* Impact Stats */}
                <div className="grid grid-cols-2 gap-6 mt-8 pt-8 border-t border-gray-200">
                  <div>
                    <div className="text-3xl font-serif text-[#395958] mb-2">120+</div>
                    <div className="text-sm text-gray-600">Women Entrepreneurs</div>
                  </div>
                  <div>
                    <div className="text-3xl font-serif text-[#395958] mb-2">2 Days</div>
                    <div className="text-sm text-gray-600">Of Celebration</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, margin: '-100px' }}
            >
              <div className="text-xs uppercase tracking-[0.4em] text-[#395958] mb-6">Partnerships</div>
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-6 leading-tight">
                Collaborating for Greater Impact
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                We believe in the power of collaboration. Our partnerships with NGOs, government agencies,
                and community organizations amplify our impact and ensure sustainable change. Together,
                we're building a network of positive influence that extends far beyond individual celebrations.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#F1D291] rounded-full"></div>
                  <span className="text-sm text-gray-700">Strategic partnerships with 50+ NGOs</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#F1D291] rounded-full"></div>
                  <span className="text-sm text-gray-700">Government collaboration on rural development</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#F1D291] rounded-full"></div>
                  <span className="text-sm text-gray-700">Community-driven program implementation</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, margin: '-100px' }}
              className="relative"
            >
              <video
                ref={videoRef}
                src="/navya/navya.mp4"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
                autoPlay
                muted
                loop
                playsInline
              />

            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-[#395958] to-[#2a4342]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: '-100px' }}
          >
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-6 leading-tight">
              Join Us in Making a Difference
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Whether you're planning your wedding or looking to support our CSR initiatives,
              every celebration can contribute to positive change.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="px-8 py-4 bg-[#F1D291] text-[#395958] text-sm uppercase tracking-[0.3em] font-medium hover:bg-white transition-colors duration-300"
              >
                Partner With Us
              </a>
              <a
                href="/contact"
                className="px-8 py-4 border-2 border-white text-white text-sm uppercase tracking-[0.3em] font-medium hover:bg-white hover:text-[#395958] transition-colors duration-300"
              >
                Learn More
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}